import React, { useRef, useEffect, useState, useCallback } from "react";
import { ShapeSvg } from "@/components/ShapeSvg";

const SIZE = 320;
const SHAPE_PATHS = {
  circle: (c) => { c.arc(160, 160, 120, 0, Math.PI * 2); },
  square: (c) => { c.rect(50, 50, 220, 220); },
  triangle: (c) => { c.moveTo(160, 40); c.lineTo(290, 280); c.lineTo(30, 280); c.closePath(); },
  rectangle: (c) => { c.rect(30, 90, 260, 140); },
  star: (c) => { for (let i = 0; i < 10; i++) { const r = i % 2 ? 55 : 130, a = -Math.PI / 2 + (i * Math.PI) / 5; c[i ? "lineTo" : "moveTo"](160 + r * Math.cos(a), 165 + r * Math.sin(a)); } c.closePath(); },
  heart: (c) => { c.moveTo(160, 280); c.bezierCurveTo(30, 190, 20, 100, 90, 70); c.bezierCurveTo(130, 55, 160, 90, 160, 110); c.bezierCurveTo(160, 90, 190, 55, 230, 70); c.bezierCurveTo(300, 100, 290, 190, 160, 280); },
  diamond: (c) => { c.moveTo(160, 30); c.lineTo(290, 160); c.lineTo(160, 290); c.lineTo(30, 160); c.closePath(); },
  oval: (c) => { c.ellipse(160, 160, 135, 90, 0, 0, Math.PI * 2); },
};
export const TRACE_SHAPES = Object.keys(SHAPE_PATHS);

function drawGuide(ctx, item, style) {
  ctx.clearRect(0, 0, SIZE, SIZE);
  ctx.save();
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  if (item.shape) {
    ctx.beginPath(); SHAPE_PATHS[item.shape](ctx);
    if (style === "mask") { ctx.lineWidth = 34; ctx.strokeStyle = "#000"; ctx.stroke(); }
    else { ctx.lineWidth = 26; ctx.strokeStyle = "#EDE9FE"; ctx.stroke(); ctx.setLineDash([2, 14]); ctx.lineWidth = 6; ctx.strokeStyle = "#A78BFA"; ctx.stroke(); }
  } else {
    ctx.font = `bold 240px 'Fredoka', 'Nunito', sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    if (style === "mask") { ctx.fillStyle = "#000"; ctx.fillText(item.text, 160, 175); }
    else { ctx.fillStyle = "#EDE9FE"; ctx.fillText(item.text, 160, 175); ctx.setLineDash([2, 12]); ctx.lineWidth = 5; ctx.strokeStyle = "#A78BFA"; ctx.strokeText(item.text, 160, 175); }
  }
  ctx.restore();
}

/* item: {text} | {shape}. onComplete(coverage) fires once when coverage >= threshold */
export function TraceCanvas({ item, onComplete, threshold = 0.55 }) {
  const guideRef = useRef(null), drawRef = useRef(null), maskRef = useRef(null), userRef = useRef(null);
  const [coverage, setCoverage] = useState(0);
  const [complete, setComplete] = useState(false);
  const drawing = useRef(false), last = useRef(null), doneRef = useRef(false), moveCount = useRef(0);

  const reset = useCallback(() => {
    const g = guideRef.current.getContext("2d"); drawGuide(g, item, "guide");
    const m = (maskRef.current ||= document.createElement("canvas")); m.width = m.height = SIZE; drawGuide(m.getContext("2d", { willReadFrequently: true }), item, "mask");
    const u = (userRef.current ||= document.createElement("canvas")); u.width = u.height = SIZE; u.getContext("2d", { willReadFrequently: true }).clearRect(0, 0, SIZE, SIZE);
    drawRef.current.getContext("2d").clearRect(0, 0, SIZE, SIZE);
    setCoverage(0); setComplete(false); doneRef.current = false;
  }, [item]);
  useEffect(() => { reset(); }, [reset]);

  const measure = () => {
    const mask = maskRef.current.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, SIZE, SIZE).data;
    const user = userRef.current.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, SIZE, SIZE).data;
    let total = 0, hit = 0;
    for (let i = 3; i < mask.length; i += 4 * 3) { if (mask[i] > 100) { total++; if (user[i] > 50) hit++; } }
    const cov = total ? hit / total : 0;
    setCoverage(cov);
    if (cov >= threshold && !doneRef.current) { doneRef.current = true; setComplete(true); onComplete?.(cov); }
  };

  const pos = (e) => { const r = drawRef.current.getBoundingClientRect(); const s = SIZE / r.width; return { x: (e.clientX - r.left) * s, y: (e.clientY - r.top) * s }; };
  const start = (e) => { drawing.current = true; last.current = pos(e); drawRef.current.setPointerCapture?.(e.pointerId); };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e), l = last.current;
    const c = drawRef.current.getContext("2d"); c.lineCap = "round"; c.lineJoin = "round"; c.lineWidth = 22; c.strokeStyle = "#8B5CF6";
    c.beginPath(); c.moveTo(l.x, l.y); c.lineTo(p.x, p.y); c.stroke();
    const u = userRef.current.getContext("2d"); u.lineCap = "round"; u.lineWidth = 30; u.strokeStyle = "#000";
    u.beginPath(); u.moveTo(l.x, l.y); u.lineTo(p.x, p.y); u.stroke();
    last.current = p;
    if ((moveCount.current = (moveCount.current + 1) % 8) === 0) measure();
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; measure(); };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className={`relative rounded-3xl bg-white shadow-xl border-4 ${complete ? "border-emerald-300" : "border-purple-200"}`} style={{ width: "min(320px, 85vw)", aspectRatio: "1" }}>
        <canvas ref={guideRef} width={SIZE} height={SIZE} className="absolute inset-0 w-full h-full rounded-3xl" />
        <canvas ref={drawRef} width={SIZE} height={SIZE} data-testid="trace-canvas" className="absolute inset-0 w-full h-full rounded-3xl touch-none cursor-crosshair"
          onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} />
        {complete && <div className="absolute -top-4 -right-4 text-5xl ll-pop">⭐</div>}
      </div>
      <div className="w-full max-w-[320px] ll-progress" data-testid="trace-coverage"><span style={{ width: `${Math.min(100, Math.round((coverage / threshold) * 100))}%` }} /></div>
      <button data-testid="trace-clear-button" onClick={reset} className="ll-btn ll-btn-secondary py-2 text-sm">Clear</button>
    </div>
  );
}

export function ShapePreview({ shape }) { return <ShapeSvg shape={shape} size={40} />; }
